import { cloneElement, forwardRef, useCallback, useMemo, useRef } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import cx from 'classnames';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { useDensity } from '../Density';
import { getListTokens } from './__internal__';
import { useListKeyDownHandler } from './useListKeyDownHandler';
import cn from './List.css.json';
import './List.css';
import { jsxs as _jsxs } from "react/jsx-runtime";
const useHtmlId = createUseHtmlId('groupedListDesign');
export const GroupedListDesign = /*#__PURE__*/forwardRef(function GroupedListDesign({
  id,
  testId,
  ariaLabelledBy,
  ariaDescribedBy,
  ariaLabel,
  groups,
  className,
  theme,
  font,
  variant,
  whiteSpace,
  style,
  role,
  focusable,
  selectedIndex,
  setFocusedIndex,
  onSelectFocusedItem,
  nullItem
}, ref) {
  const listId = id ?? useHtmlId();
  const listRef = useRef(null);
  const dt = useDesignTokens();
  const {
    density
  } = useDensity();
  const tokenStyle = useMemo(() => getListTokens(dt, density, theme, font, variant, whiteSpace), [dt, density, theme, font, variant, whiteSpace]);
  const nullItemId = (nullItem?.props.id ?? `${listId}-null-item`).replace(/\s+/g, '');
  const getGroupId = useCallback((group, index) => (group.id ?? `${listId}-group-${index}`).replace(/\s+/g, ''), [listId]);
  const getItemId = (groupId, item, index) => item.props.id ?? `${groupId}-item-${index}`;
  const activeDescendant = useMemo(() => {
    if (isNullish(selectedIndex)) return undefined;
    if (nullItem && selectedIndex === 0) {
      return nullItemId;
    }
    let count = nullItem ? 1 : 0;
    for (const group of groups) {
      if (!group.items) continue;
      for (const child of group.items) {
        if (count === selectedIndex) {
          const groupId = getGroupId(group, groups.indexOf(group));
          return getItemId(groupId, child, group.items.indexOf(child));
        }
        count++;
      }
    }
  }, [getGroupId, groups, nullItem, nullItemId, selectedIndex]);
  const handleKeyDown = useListKeyDownHandler({
    length: groups.reduce((acc, group) => acc + (group.items?.length || 0), 0) + (nullItem ? 1 : 0),
    selectedIndex,
    setFocusedIndex,
    onSelectFocusedItem,
    ulRef: listRef
  });
  return /*#__PURE__*/_jsxs("div", {
    ref: ul => {
      listRef.current = ul;
      setRef(ref, ul);
    },
    role: role ?? 'listbox',
    id: listId,
    "data-testid": testId,
    className: className,
    style: {
      ...tokenStyle,
      ...style
    },
    "aria-label": ariaLabel,
    "aria-labelledby": Array.isArray(ariaLabelledBy) ? ariaLabelledBy.join(' ') : ariaLabelledBy,
    "aria-describedby": Array.isArray(ariaDescribedBy) ? ariaDescribedBy.length ? ariaDescribedBy.join(' ') : undefined : ariaDescribedBy,
    "aria-activedescendant": activeDescendant,
    tabIndex: focusable === false ? -1 : undefined,
    onKeyDown: handleKeyDown,
    children: [nullItem && /*#__PURE__*/cloneElement(nullItem, {
      key: nullItem.key ?? 'null-item',
      id: nullItemId
    }), groups.map((group, index) => {
      const groupId = getGroupId(group, index);
      const groupHeaderId = group.header?.props.id ?? `${groupId}-header`;
      return /*#__PURE__*/_jsxs("ul", {
        id: groupId,
        "data-testid": group.testId,
        role: "group",
        "aria-labelledby": group.header ? groupHeaderId : undefined,
        "aria-label": group.ariaLabel ?? (!group.header ? `${index + 1}` : undefined),
        className: cx(!!theme && cn.themeOverride, cn.list, !group.header && index !== 0 && cn.groupTopSeparator),
        children: [!group.header ? undefined : /*#__PURE__*/cloneElement(group.header, {
          key: groupHeaderId,
          id: groupHeaderId,
          className: cx(nullItem || index !== 0 ? cn.itemTopSeparator : undefined)
        }), group.items?.map((item, itemIndex) => {
          const itemKey = item.key ?? `${groupId}-item-${itemIndex}`;
          return /*#__PURE__*/cloneElement(item, {
            key: itemKey,
            id: getItemId(groupId, item, itemIndex)
          });
        })]
      }, groupId);
    })]
  });
});
//# sourceMappingURL=GroupedListDesign.js.map